import type { ElectronMonitoringData } from '@carboncopyco/types/desktop';
import { useState } from 'react';
import { AppEventsEnum } from '../../../shared/enums/AppEventsEnum';
import useElectronListener from '../../hooks/useElectronListener';

const ElectronMonitoring = () => {
  const [electronMonitoringData, setElectronMonitoringData] =
    useState<ElectronMonitoringData>();

  const numberOfAllElectronListeners = electronMonitoringData?.listeners.reduce(
    (acc, listener) => acc + listener.listeners,
    0
  );

  const handleElectronMonitoringData = (...args: unknown[]) => {
    const dataToSet = args[0] as unknown as ElectronMonitoringData;
    setElectronMonitoringData(dataToSet);
  };

  useElectronListener(
    AppEventsEnum.MAIN_TO_RENDERER_MONITORING_DATA,
    handleElectronMonitoringData
  );

  return (
    <>
      <details>
        <summary>
          Views (<strong>{electronMonitoringData?.views.length || 0}</strong>)
        </summary>

        {electronMonitoringData && electronMonitoringData?.views.length > 0 ? (
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400 mt-4">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3"
                >
                  Tab id
                </th>
                <th
                  scope="col"
                  className="px-6 py-3"
                >
                  Tab type
                </th>
                <th
                  scope="col"
                  className="px-6 py-3"
                >
                  Tab title
                </th>
              </tr>
            </thead>
            <tbody>
              {electronMonitoringData.views.map((view) =>
                view.tabId ? (
                  <tr
                    key={view.tabId}
                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
                  >
                    <th
                      scope="row"
                      className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                    >
                      {view.tabId}
                    </th>
                    <td className="px-6 py-4">{view.tabType}</td>
                    <td className="px-6 py-4">{view.tabTitle}</td>
                  </tr>
                ) : null
              )}
            </tbody>
          </table>
        ) : (
          <p className="mt-4">No views open</p>
        )}
      </details>

      <hr className="my-6" />
      <h3>
        Number of interceptors:{' '}
        <strong>{electronMonitoringData?.numberOfInterceptors || 0}</strong>
      </h3>
      <hr className="my-6" />
      <details>
        <summary>
          interceptor Data (
          <strong>{electronMonitoringData?.interceptors.length || 0}</strong>)
        </summary>

        {electronMonitoringData &&
        electronMonitoringData?.interceptors.length > 0 ? (
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400 mt-4">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3"
                >
                  Inner Tab Id
                </th>
                <th
                  scope="col"
                  className="px-6 py-3"
                >
                  Interceptor Type
                </th>
                <th
                  scope="col"
                  className="px-6 py-3"
                >
                  Session Id
                </th>
                <th
                  scope="col"
                  className="px-6 py-3"
                >
                  Token
                </th>
                <th
                  scope="col"
                  className="px-6 py-3"
                >
                  Is Cache Enabled
                </th>
                <th
                  scope="col"
                  className="px-6 py-3"
                >
                  Cached Number Of Items
                </th>
                <th
                  scope="col"
                  className="px-6 py-3"
                >
                  Cache Size
                </th>
              </tr>
            </thead>
            <tbody>
              {electronMonitoringData.interceptors.map((interceptor) =>
                interceptor.innerTabId ? (
                  <tr
                    key={interceptor.innerTabId}
                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
                  >
                    <td className="px-6 py-4">{interceptor.innerTabId}</td>
                    <td className="px-6 py-4">{interceptor.interceptorType}</td>
                    <td className="px-6 py-4">{interceptor.sessionId}</td>
                    <td className="px-6 py-4">{interceptor.token}</td>
                    <td className="px-6 py-4">
                      {interceptor.isCacheEnabled ? 'true' : 'false'}
                    </td>
                    <td className="px-6 py-4">{interceptor.numberOfItems}</td>
                    <td className="px-6 py-4">
                      {interceptor.calculatedSize} / {interceptor.maxCacheSize}
                    </td>
                  </tr>
                ) : null
              )}
            </tbody>
          </table>
        ) : (
          <p className="mt-4">No interceptors open</p>
        )}
      </details>
      <hr className="my-6" />
      <details>
        <summary>
          Recorder Manager Data (
          <strong>
            {electronMonitoringData?.recorderManagerData.length || 0}
          </strong>
          )
        </summary>
        {electronMonitoringData &&
        electronMonitoringData?.recorderManagerData.length > 0 ? (
          <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400 mt-4">
            <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3"
                >
                  Tab Id
                </th>
                <th
                  scope="col"
                  className="px-6 py-3"
                >
                  Session Id
                </th>
                <th
                  scope="col"
                  className="px-6 py-3"
                >
                  is Queue Empty
                </th>
                <th
                  scope="col"
                  className="px-6 py-3"
                >
                  Queue Size
                </th>
              </tr>
            </thead>
            <tbody>
              {electronMonitoringData.recorderManagerData.map((service) =>
                service.tabId ? (
                  <tr
                    key={service.tabId}
                    className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
                  >
                    <td className="px-6 py-4">{service.tabId}</td>
                    <td className="px-6 py-4">{service.sessionId}</td>
                    <td className="px-6 py-4">
                      {service.isQueueEmpty ? 'true' : 'false'}
                    </td>
                    <td className="px-6 py-4">{service.queueSize}</td>
                  </tr>
                ) : null
              )}
            </tbody>
          </table>
        ) : (
          <p className="mt-4">No interceptors open</p>
        )}
      </details>
      <hr className="my-6" />
      <details>
        <summary>
          Channels (
          <strong>{electronMonitoringData?.listeners.length || 0}</strong>) and
          listeners (<strong>{numberOfAllElectronListeners}</strong>)
        </summary>

        <table className="w-full text-sm text-left text-gray-500 dark:text-gray-400 mt-4">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th
                scope="col"
                className="px-6 py-3"
              >
                Channel ({electronMonitoringData?.listeners.length || 0})
              </th>
              <th
                scope="col"
                className="px-6 py-3"
              >
                Number of listeners ({numberOfAllElectronListeners})
              </th>
            </tr>
          </thead>
          <tbody>
            {electronMonitoringData?.listeners.map((listener) => (
              <tr
                key={listener.channel}
                className="bg-white border-b dark:bg-gray-800 dark:border-gray-700"
              >
                <th
                  scope="row"
                  className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white"
                >
                  {listener.channel}
                </th>
                <td className="px-6 py-4">{listener.listeners || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </details>
    </>
  );
};

export default ElectronMonitoring;
